import { Boot } from './Scenes/Boot';
import { AUTO, Game } from 'phaser';
import { Preloader } from './Scenes/Preloader';
import NoteSequenceExercise from './Scenes/NoteSequenceExercise';
import { ExerciseTempoPractice } from './Scenes/ExerciseTempoPractice';
import { PianoScene } from './Scenes/Piano';
import { PianoDebugScene } from './Scenes/PianoDebug';
import { TestExercise } from './Scenes/TestExercise';
import PauseMenu from './Scenes/PauseMenu';
import { Dissolve, Wipe } from './Rendering/Transitions';
import Empty from './Scenes/Empty';
import StaffExercise from './Scenes/StaffExercise';
import NoTimeExercise from './Scenes/NoTime';
//import SightReadingMobile from './Scenes/SightReadingMobile';
import SightReadingPhaser from './Scenes/SightReadingPhaser';
import OSMDSightReadingScene from './Scenes/OSMDSightReadingScene';
import OSMDRepertoireScene from './Scenes/OSMDRepertoireScene';
import { isCapacitorNative } from 'Utils/Constants';

// Phaser's own centring, with one change: the canvas never rises above the top
// of its play area. Phaser centres by writing a top margin of half the free
// height, which is negative once the play area is shorter than the canvas
// (e.g. a lesson opened in fullscreen, then fullscreen is left). That slides
// the canvas up over the header. Holding the margin at zero keeps the canvas
// at the top of the play area, so the extra height is cut off at the bottom.
const centringHeldBelowPlayAreaTop = (scale: Phaser.Scale.ScaleManager) => {
  const centreCanvas = scale.updateCenter.bind(scale);
  return (): void => {
    centreCanvas();
    const canvasStyle = scale.canvas.style;
    if (parseFloat(canvasStyle.marginTop) < 0) {
      canvasStyle.marginTop = '0px';
    }
  };
};

const StartGame = (
  parent: string,
  width: number,
  height: number,
  scaling?: number,
  crisp?: boolean,
) => {
  //  Find out more information about the Game Config at:
  //  https://newdocs.phaser.io/docs/3.70.0/Phaser.Types.Core.GameConfig
  const dpr = window.devicePixelRatio;
  console.log('dpr: ', dpr);
  const config: Phaser.Types.Core.GameConfig = {
    type: AUTO,
    antialias: true,
    scale: {
      mode: scaling ?? Phaser.Scale.FIT,
      // Centre the canvas vertically in its parent. Without this the canvas
      // is pinned to the top, so any parent/aspect change (e.g. entering
      // fullscreen) leaves the free height below the music instead of
      // splitting it. Vertical only: the canvas is an inline element under
      // `.App { text-align: center }`, so CSS already centres it horizontally,
      // and a Phaser margin-left on top of that pushes it right of centre.
      autoCenter: Phaser.Scale.CENTER_VERTICALLY,
      width: crisp ? width * dpr : width,
      height: crisp ? height * dpr : height,
    },
    callbacks: {
      // Runs before Phaser boots, so every centring pass goes through the
      // held version, including the first.
      preBoot: (game) => {
        game.scale.updateCenter = centringHeldBelowPlayAreaTop(game.scale);
      },
    },
    transparent: true,
    width,
    height,
    parent,
    autoRound: true,
    antialiasGL: true,
    loader: {
      // A stalled cold-cache request must terminate so Preloader can either
      // retry or continue after Phaser reports the failed asset.
      timeout: 15_000,
      maxRetries: 1,
    },
    // iOS audio is owned by MuseFlowAudio/AVAudioEngine. Creating a second
    // Phaser WebAudio graph wastes WKWebView memory and has historically made
    // iOS audio-session interruptions less predictable.
    audio: { noAudio: isCapacitorNative() },
    scene: [
      Boot,
      Preloader,
      NoteSequenceExercise,
      ExerciseTempoPractice,
      PianoScene,
      PianoDebugScene,
      TestExercise,
      PauseMenu,
      StaffExercise,
      NoTimeExercise,
      Empty,
      //SightReadingMobile,
      SightReadingPhaser,
      OSMDSightReadingScene,
      OSMDRepertoireScene,
    ],
    pipeline: {
      Dissolve: Dissolve,
      Wipe: Wipe,
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- Phaser pipeline registry
  };
  return new Game(config);
};

export default StartGame;
